import { Pane, PaneScrollable, PaneWrapper } from '@campfire/pane';
import { Box, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { Form, Formik } from 'formik';
import React, { useMemo } from 'react';
import { useHistory } from 'react-router';
import { BooleanParam, useQueryParams } from 'use-query-params';
import { boolean as YupBool, object as YupObject, string as YupString } from 'yup';
import { StickyHeader } from '../../../../../common/StickyHeader';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { ReportTypeFormSections } from '../../common/report-type-form-sections/ReportTypeFormSections';
import { DEFAULT_RETURN_SLUG } from '../../ReportManagementConsts';
import { REPORT_MANAGEMENT_GET_SINGLE_REPORT_TYPE } from '../report-types-model.gql';
import { parseReportTypeInitialValues } from '../ReportTypeUtils';
import {
  ReportManagementGetSingleReportType,
  ReportManagementGetSingleReportTypeVariables,
} from '../__generated__/ReportManagementGetSingleReportType';
import { useReportTypeActions } from './report-type-form-actions';

const ValidationSchema = YupObject().shape({
  reportTypeId: YupString(),
  reportTypeName: YupString().required('Please provide a name'),
  active: YupBool().required(),
});

export const EditReportTypeForm = ({ reportTypeId }: { reportTypeId: string }) => {
  const [query, setQuery] = useQueryParams({
    editReportType: BooleanParam,
  });

  const { data: getReportTypeData, loading: getReportTypeLoading } = useCampfireQuery<
    ReportManagementGetSingleReportType,
    ReportManagementGetSingleReportTypeVariables
  >(REPORT_MANAGEMENT_GET_SINGLE_REPORT_TYPE, {
    options: {
      variables: {
        reportTypeId: reportTypeId,
        withForm: true,
      },
    },
  });

  const initialValues = useMemo(() => {
    return parseReportTypeInitialValues(getReportTypeData?.vm?.reportType ?? undefined);
  }, [getReportTypeData]);

  const history = useHistory();
  const { runSaveReportType, runSaveReportTypeLoading } = useReportTypeActions();

  const handleSaveReportSuccess = (newReportTypeId: string) => {
    history.replace(`${DEFAULT_RETURN_SLUG}?tab=report-types&reportTypeId=${newReportTypeId}`);
  };

  return getReportTypeLoading ? (
    <Box paddingX={3} paddingY={5}>
      <Skeleton variant='text' width={350} height={25} />
      <Skeleton variant='text' width={200} height={15} />
    </Box>
  ) : (
    <Formik
      initialValues={initialValues}
      validationSchema={ValidationSchema}
      onSubmit={(values) => runSaveReportType({ values, handleSuccess: handleSaveReportSuccess })}
    >
      {() => {
        return (
          <PaneWrapper>
            <Pane>
              <Form style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', height: 'auto' }}>
                <StickyHeader
                  handleDiscard={() => setQuery({ ...query, editReportType: undefined })}
                  saveButtonName='Save Report Type'
                  closeButtonName='Cancel'
                  saveButtonProps={{
                    variant: 'contained',
                    color: 'primary',
                    disabled: runSaveReportTypeLoading,
                  }}
                />
                <PaneScrollable>
                  <Box display='flex' flex='1 1 auto' flexDirection='column' padding={4} height='auto'>
                    <Typography variant='h4' component='h1' display='inline'>
                      Report Type
                    </Typography>
                    <ReportTypeFormSections />
                  </Box>
                </PaneScrollable>
              </Form>
            </Pane>
          </PaneWrapper>
        );
      }}
    </Formik>
  );
};
